/** Slice for authentication related data. */
import {createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { RootState } from "../store";
import {jwtDecode} from "jwt-decode";

export interface OrganizationWhitelabelDetails {
  logo_url?: string | null;
  frontend_domain?: string | null;
  name?: string | null;
}

export interface OrganizationDetails {
  feature_flags: Record<string, any>;
  bulk_enrollment_limit: number;
  last_sunset_at?: string;
  // Present only for whitelabel (agency) accounts; null/undefined otherwise
  whitelabel?: OrganizationWhitelabelDetails | null;
}

export interface UserDetails {
  email: string;
  organization?: OrganizationDetails;
  mfa_enabled?: boolean;
}

export interface AuthUser {
  accessToken: string;
  userDetails?: UserDetails;
  mfaPending?: boolean;
  mfaToken?: string;
  isOperatorSession?: boolean;
}

const initialState: AuthUser = {
  accessToken: "",
  mfaPending: false,
  mfaToken: undefined,
};

const authUserSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    setAccessToken(state, action: PayloadAction<string>) {
      state.accessToken = action.payload;
    },
    setUserDetails(state, action: PayloadAction<UserDetails>) {
      state.userDetails = action.payload;
    },
    setIsOperatorSession(state, action: PayloadAction<boolean>) {
      state.isOperatorSession = action.payload;
    },
    purgeAuthUser(state) {
      state.accessToken = "";
      state.userDetails = undefined;
      state.mfaPending = false;
      state.mfaToken = undefined;
      state.isOperatorSession = false;
    },
    setMfaPending(state, action: PayloadAction<{ mfaToken: string }>) {
      state.mfaPending = true;
      state.mfaToken = action.payload.mfaToken;
    },
    clearMfaPending(state) {
      state.mfaPending = false;
      state.mfaToken = undefined;
    }
  },
});

// Actions
export const {setAccessToken, setUserDetails, purgeAuthUser, setMfaPending, clearMfaPending, setIsOperatorSession} = authUserSlice.actions;

// Selectors

/**
 * User is authenticated if they have a valid access token and the token has not expired.
 */
export const selectAuthenticated = (state: RootState): boolean => {
  const hasAccessToken = state.authUser.accessToken !== "";
  if (!hasAccessToken) return false;
  const decoded = jwtDecode(state.authUser?.accessToken);
  const accessTokenHasExpired = decoded.exp && decoded.exp < Date.now() / 1000;
  return !accessTokenHasExpired;
}
/**
 * Get the cached access token.
 */
export const selectAccessToken = (state: RootState): string => state.authUser.accessToken;

export const selectUserDetails = (state: RootState): UserDetails | undefined => state.authUser.userDetails;

export const selectOrganization = (state: RootState): OrganizationDetails | undefined => state.authUser.userDetails?.organization;

export const selectUserEmail = createSelector(
    [(state: RootState) => state.authUser.userDetails],
    (userDetails) => userDetails?.email
);

/**
 * Return a boolean indicating if the access token is almost expired. We'll use
 * this info to decide if we should refresh the token.
 * 
 * If true is returned, user is still authenticated and within the token expiration window.
 */
const TOKEN_EXPIRATION_WINDOW_IN_MINUTES = 60; // we'll refresh the token if it expires in the next 60 minutes
export const selectTokenIsAlmostExpired = createSelector(
  [selectAuthenticated, selectAccessToken],
  (authenticated, accessToken) => {
    // if the user is not authenticated, the token is not almost expired - it's fully expired
    if (!authenticated) return false;

    const decoded = jwtDecode(accessToken);
    // consider almost expired if the token expires in the next TOKEN_EXPIRATION_WINDOW_IN_MINUTES minutes
    return decoded.exp && decoded.exp < (Date.now() / 1000) + TOKEN_EXPIRATION_WINDOW_IN_MINUTES * 60;
  }
);


/**
 * Get the user ID (sub) from the access token.
 */
export const selectUserId = createSelector(
  [selectAccessToken],
  (accessToken) => {
    if (accessToken === "") return undefined;
    // decode the access token to get the user ID
    const decoded = jwtDecode(accessToken);
    return decoded.sub;
  }
);

export const selectMfaPending = (state: RootState): boolean => !!state.authUser.mfaPending;
export const selectMfaToken = (state: RootState): string | undefined => state.authUser.mfaToken;
export const selectIsOperatorSession = (state: RootState): boolean => {
  // Derive operator status from the CURRENT access token's `operator_session`
  // claim (same source the backend `require_operator!` reads), NOT from the
  // persisted `isOperatorSession` flag. That flag is only ever set to `true`
  // and survives via redux-persist, so it goes stale after a normal re-login
  // or token refresh — leaving the operator-only nav visible while the API
  // rejects with 403. Reading the token keeps frontend and backend in sync.
  const accessToken = state.authUser.accessToken;
  if (!accessToken) return false;
  try {
    const decoded = jwtDecode(accessToken) as { operator_session?: boolean };
    return !!decoded.operator_session;
  } catch {
    return false;
  }
};

// Reducer
export default authUserSlice.reducer;
